/**
 * Tooltip content for Strategic Framework components
 * Provides user-friendly explanations with practical examples
 */

export const strategicFrameworkTooltips = {
  // Section Headers
  sections: {
    objectives: {
      title: "Strategic Objectives",
      content: "High-level strategic goals that define what your organization wants to achieve. These are the key outcomes that drive your business forward.",
      example: "Example: 'Increase market share by 15%', 'Achieve 90% customer satisfaction', 'Launch 3 new product lines'"
    },
    initiatives: {
      title: "Strategic Initiatives",
      content: "Specific projects and actions that help achieve your strategic objectives. Each initiative is linked to one or more objectives and has defined timelines and budgets.",
      example: "Example: 'Digital Marketing Campaign', 'Product Development Project', 'Staff Training Program'"
    },
    revenueStreams: {
      title: "Revenue Streams",
      content: "Different ways your business generates income. Each stream is linked to initiatives and represents a distinct source of revenue with its own pricing model.",
      example: "Example: 'Product Sales', 'Subscription Revenue', 'Consulting Services', 'Licensing Fees'"
    },
    products: {
      title: "Products & Services",
      content: "Individual products or services you offer, linked to revenue streams. Track pricing, costs, volumes, and growth rates for each offering.",
      example: "Example: 'Premium Software License', 'Basic Consulting Package', 'Enterprise Support Plan'"
    },
    sales: {
      title: "Sales Analytics",
      content: "Comprehensive analysis of your sales performance including growth projections, revenue forecasts, and portfolio analytics across all products and services.",
      example: "Shows year-by-year projections, growth rates (CAGR), and revenue contributions by product"
    },
    costAnalysis: {
      title: "Cost Analysis",
      content: "Strategic view of how costs are allocated across your objectives and initiatives. Analyze ROI and cost efficiency of your strategic investments.",
      example: "See which objectives consume the most resources and their return on investment"
    }
  },
  
  // Objectives Tab
  objectives: {
    strategicPillar: "Core focus area for your strategic planning. Example: 'Financial Performance' tracks revenue growth and profitability targets.",
    objectiveName: "Specific goal you want to achieve. Example: 'Increase market share by 15% in East Africa'",
    description: "Detailed explanation of the objective. Example: 'Expand into Kenya and Tanzania markets through local partnerships'",
    owner: "Person responsible for achieving this objective. Example: 'Sarah Johnson - VP of Sales'",
    targetDate: "Deadline for achieving this objective. Example: December 31, 2026",
    status: "Current progress status. Example: 'In Progress' means actively working on it",
    objectiveId: "Unique identifier for tracking. Example: OBJ-001, OBJ-002. Auto-generated if left blank."
  },

  // Initiatives Tab
  initiatives: {
    linkedObjective: "Which strategic objective does this initiative support? Example: Link 'Digital Marketing Campaign' to 'Increase Market Share' objective",
    initiativeName: "Specific action or project name. Example: 'Launch Instagram Advertising Campaign'",
    description: "What will be done and how. Example: 'Run targeted ads focusing on ages 25-40 in urban areas'",
    budget: "Total investment needed for this initiative. Example: $25,000 for 6-month campaign",
    timelineStart: "When this initiative begins. Example: January 1, 2026",
    timelineEnd: "When this initiative completes. Example: June 30, 2026",
    owner: "Person leading this initiative. Example: 'Michael Chen - Marketing Manager'",
    status: "Current state of the initiative. Example: 'Planning' = preparing to launch",
    initiativeId: "Unique tracking code. Example: INI-001, INI-002. Auto-generated if blank."
  },

  // Revenue Streams Tab
  revenueStreams: {
    linkedObjective: "Which strategic objective does this revenue stream support? Example: Link 'Product Sales Revenue' to 'Market Expansion' objective",
    linkedInitiative: "Which initiative generates this revenue? Example: Link 'Product Sales' to 'Product Launch' initiative",
    streamName: "Name of the revenue source. Example: 'Premium Subscription Sales'",
    revenueType: "How money is earned. Example: 'Subscription Revenue' = recurring monthly payments",
    pricingModel: "How pricing works. Example: 'Per User/Month' = charge per active user",
    basePrice: "Starting price point. Example: $49/month per user",
    projectedVolume: "Expected sales quantity. Example: 500 subscribers in Year 1",
    description: "Revenue stream details. Example: 'Monthly subscriptions for premium features package'",
    streamId: "Unique identifier. Example: REV-001, REV-002. Auto-generated if blank.",
    owner: "Person responsible for this revenue stream. Example: 'Michael Chen - Sales Director'"
  },

  // Products Tab
  products: {
    linkedRevenueStream: "Which revenue stream does this product belong to? Example: Link 'Premium Plan' to 'Subscription Revenue' stream",
    productName: "Product or service name. Example: 'Enterprise Software License'",
    productType: "Category of offering. Example: 'Software as a Service (SaaS)'",
    unitPrice: "Price per unit sold. Example: $99 per license",
    cogs: "Cost to produce/deliver one unit. Example: $15 per license (hosting + support)",
    unitsSold: "Quantity sold per period. Example: 100 licenses per month",
    productId: "Unique code. Example: PRD-001, PRD-002. Auto-generated if blank."
  },

  // Sales Tab
  sales: {
    annualGrowthRate: "Expected revenue increase per year. Example: Year 1: 15%, Year 2: 20%, Year 3: 12%",
    cagr: "Compound Annual Growth Rate - average growth across all years. Example: 15.5% CAGR means revenue grows by this rate on average",
    marketPenetration: "Percentage of target market captured. Example: 5% means reaching 5 out of every 100 potential customers",
    customerAcquisition: "New customers gained per period. Example: 50 new customers per month"
  },

  // Cost Analysis Tab
  costAnalysis: {
    strategicCostAllocation: "How costs are distributed across strategic objectives. Example: 40% to Growth, 30% to Operations, 30% to Innovation",
    costByInitiative: "Total spending per initiative. Example: 'Digital Marketing' costs $50,000 annually",
    roiAnalysis: "Return on investment calculation. Example: Spend $50K, generate $150K revenue = 200% ROI"
  }
};

// Cost Management Tooltips
export const costManagementTooltips = {
  // Core Operating Costs
  fixedCosts: {
    category: "Costs that don't change with sales volume. Example: Rent is $5,000/month regardless of how much you sell",
    itemName: "Specific fixed expense. Example: 'Office Rent', 'Software Licenses', 'Manager Salaries'",
    amount: "How much you pay per period. Example: $5,000 per month for office space",
    frequency: "How often you pay. Example: Monthly, Quarterly, Annually"
  },

  variableCosts: {
    category: "Costs that change with sales volume. Example: If you sell more products, you buy more raw materials",
    itemName: "Specific variable expense. Example: 'Raw Materials', 'Sales Commissions', 'Packaging'",
    calculationType: "How cost is calculated. Example: 'Per Unit' = $5 per product sold",
    rate: "Cost per unit/transaction. Example: $5 per product or 10% of revenue"
  },

  semiVariableCosts: {
    category: "Costs with both fixed and variable components. Example: Utilities have base charge + usage charge",
    fixedComponent: "The fixed portion. Example: $200 base electricity charge",
    variableRate: "The variable portion. Example: $0.15 per kilowatt-hour used",
    itemName: "Specific semi-variable expense. Example: 'Utilities', 'Phone Bills', 'Maintenance'"
  },

  directCosts: {
    category: "Costs directly traceable to specific products. Example: Wood for furniture manufacturing",
    productAssignment: "Which product this cost belongs to. Example: 'Premium Chair' needs $50 of wood",
    unitCost: "Cost per product unit. Example: $50 of materials per chair produced",
    itemName: "Direct expense name. Example: 'Wood Materials', 'Direct Labor', 'Product Packaging'"
  },

  indirectCosts: {
    category: "Shared costs across multiple products. Example: Factory rent shared by all products made there",
    allocationMethod: "How to split the cost. Example: 'By Revenue %' = product with 30% of revenue pays 30% of rent",
    totalAmount: "Total shared cost. Example: $10,000 factory rent shared across all products",
    itemName: "Indirect expense name. Example: 'Factory Rent', 'Quality Control', 'Supervision'"
  },

  capex: {
    category: "Capital expenditures - assets with multi-year benefits. Example: Buying machinery that lasts 10 years",
    assetName: "What you're buying. Example: 'Production Line Equipment', 'Delivery Trucks'",
    purchaseCost: "Total cost to acquire. Example: $500,000 for production line",
    usefulLife: "How many years it will be used. Example: 10 years for machinery",
    depreciationMethod: "How to spread cost over time. Example: 'Straight-Line' = equal amount each year",
    salvageValue: "Estimated value at end of life. Example: $50,000 scrap value after 10 years"
  },

  financialCosts: {
    category: "Interest and fees from loans/financing. Example: Interest paid on business loan",
    loanName: "Financing source. Example: 'ABC Bank Business Loan', 'Working Capital Line of Credit'",
    principal: "Amount borrowed. Example: $200,000 loan principal",
    interestRate: "Annual interest charged. Example: 8% per year",
    term: "Repayment period. Example: 60 months (5 years)",
    paymentFrequency: "How often you pay. Example: Monthly payments of principal + interest"
  },

  tax: {
    category: "Tax obligations and payments. Example: Income tax, VAT, property tax",
    taxType: "Specific tax category. Example: 'Corporate Income Tax', 'Value Added Tax (VAT)'",
    taxRate: "Percentage charged. Example: 15% VAT on all sales",
    taxBase: "What the tax applies to. Example: 'Revenue' means tax on total sales",
    deductible: "Can this reduce taxable income? Example: Business expenses are usually deductible"
  },

  otherCosts: {
    category: "Miscellaneous or special costs not fitting other categories. Example: One-time legal fees",
    itemName: "Description of the cost. Example: 'Legal Fees', 'Consulting Services', 'R&D Expenses'",
    amount: "Total cost amount. Example: $15,000 for legal consultation"
  },

  // Strategic Framework Linking
  strategicLinking: {
    objective: "Link this cost to a strategic objective. Example: Link 'Marketing Costs' to 'Market Expansion' objective",
    initiative: "Link to specific initiative. Example: Link to 'Digital Campaign' initiative under 'Market Expansion'",
    revenueStream: "Link to revenue stream this cost supports. Example: 'Subscription Acquisition Costs' link to 'Subscription Revenue' stream",
    cascading: "Select parent first, then children. Example: Choose Objective → Shows only its Initiatives → Shows only those Initiatives' Revenue Streams"
  },

  // Growth Rates
  growthRates: {
    perYear: "Set different growth rate for each year. Example: Year 1 (fast growth): 20%, Year 2 (steady): 15%, Year 3 (mature): 10%",
    compound: "Growth compounds year-over-year. Example: $100K at 10% = $110K (Year 2) → $121K (Year 3)",
    realistic: "Base projections on market research and trends. Example: Industry average is 12%, you target 15% with new strategy"
  }
};

// Helper function to get tooltip by path
export const getTooltip = (section, field) => {
  return strategicFrameworkTooltips[section]?.[field] || costManagementTooltips[section]?.[field] || '';
};
